/* eslint-disable func-names */
import React from 'react';
import { Column } from 'rbx';
import styled from 'styled-components';
import { withFormik } from 'formik';
import * as yup from 'yup';
import { toast } from 'react-toastify';

import { Input, Select } from '../../../components/elements';

yup.addMethod(yup.string, 'testNonNegativeValue', function(errorMessage) {
  return this.test(`test-non-negative-value`, errorMessage, function(value) {
    const { path, createError } = this;
    const num = +value;

    return num >= 0 || createError({ path, message: errorMessage });
  });
});

const Container = styled.div`
  &&& {
    z-index: 2000;
    .modal-background {
      background-color: rgba(13, 13, 13, 0.28);
    }
    .modal-card-body {
      height: 80vh;
      padding: 0rem 1rem;
    }
    .modal-card {
      max-height: calc(100vh - 140px);
      position: absolute;
      width: 40rem;
    }
    .modal-card-head,
    .modal-card-foot {
      border-top-left-radius: 0px;
      border-top-right-radius: 0px;
    }
    .modal-card-title {
      font-size: 1.5rem;
      font-weight: 600;
    }
    .modal-card-head {
      border-bottom: 0px;
      padding: 1.5rem 1rem;
      background: white;
    }
    .modal-card-foot {
      justify-content: flex-end;
      border: 0px;
      padding: 1rem;
      background: white;
    }
    a {
      font-size: 1rem;
    }
  }
`;

const filterOptions = [
  { value: 'cms_socials', label: 'SOCIALS' },
  { value: 'cms_cuisine_styles', label: 'CUISINE STYLES' },
  { value: 'cms_taxes', label: 'TAXES' },
  { value: 'cms_item_types', label: 'ITEM TYPES' },
  { value: 'cms_place_ambience', label: 'PLACE AMBIENCE' },
  { value: 'cms_place_business_type', label: 'PLACE BUSINESS TYPE' },
  { value: 'cms_place_dietary_info', label: 'PLACE DIETARY INFO' },
  { value: 'cms_place_dining_style', label: 'PLACE DINING STYLE' },
  { value: 'cms_place_dress_code', label: 'PLACE DRESS CODE' },
  { value: 'cms_place_good_for', label: 'PLACE GOOD FOR' },
  { value: 'cms_place_noise_level', label: 'PLACE NOISE LEVEL' },
  { value: 'cms_place_service_category', label: 'PLACE SERVICE CATEGORY' },
  { value: 'cms_place_service_type', label: 'PLACE SERVICE TYPE' },
  { value: 'cms_common_feature', label: 'COMMON FEATURE' },
  { value: 'cms_common_parking_detail', label: 'COMMON PARKING DETAIL' },
  { value: 'cms_common_public_transport', label: 'COMMON PUBLIC TRANSPORT' },
  { value: 'cms_contact_type', label: 'CONTACT TYPE' },
  { value: 'cms_contact_feedback_type', label: 'CONTACT FEEDBACK TYPE' },
  { value: 'cms_fee_value_type', label: 'FEEVALUE TYPE' },
  { value: 'cms_general_fee', label: 'GENERAL FEE' },
  { value: 'cms_subject_type', label: 'SUBJECT TYPE' },
  { value: 'cms_place_menu_deal_type', label: 'PLACE MENU DEAL TYPE' },
];

const Form = props => {
  const {
    isActive,
    onClose,
    setFieldValue,
    values,
    handleSubmit,
    loading,
    touched,
    errors,
    acl,
  } = props;

  const [isJsonView, setIsJsonView] = React.useState(false);

  return (
    <Container className={`modal ${isActive && 'is-active'}`}>
      <div className="modal-background" />
      <div className="modal-card">
        <header className="modal-card-head">
          <p className="modal-card-title">CMS DATA</p>
          <button
            type="button"
            className="delete"
            aria-label="close"
            onClick={isJsonView ? () => setIsJsonView(false) : () => onClose()}
          />
        </header>
        <section className="modal-card-body">
          {isJsonView ? (
            <pre style={{ whiteSpace: 'pre-wrap', wordBreak: 'break-word' }}>
              {JSON.stringify(acl, null, 2)}
            </pre>
          ) : (
            <>
              <Column.Group gapSize={8} multiline vcentered>
                <Column size="half">
                  <Input
                    label="Name"
                    value={values.name}
                    onChange={event =>
                      setFieldValue(`name`, event.target.value)
                    }
                    required
                    errors={errors.name}
                    touched={touched.name}
                    disableToastMessage
                  />
                </Column>
                <Column size="half">
                  <Input
                    label="Description"
                    value={values.description}
                    onChange={event =>
                      setFieldValue(`description`, event.target.value)
                    }
                    required
                    errors={errors.description}
                    touched={touched.description}
                    disableToastMessage
                  />
                </Column>
                <Column size="half">
                  <Select
                    options={filterOptions}
                    value={filterOptions.find(
                      ele => ele.value === values.cms_data_type,
                    )}
                    onChange={({ value }) => {
                      setFieldValue(`cms_data_type`, value);
                    }}
                  />
                </Column>
              </Column.Group>
            </>
          )}
        </section>

        <footer className="modal-card-foot">
          {acl && (
            <>
              {isJsonView ? (
                <button
                  type="button"
                  className="button"
                  onClick={() => setIsJsonView(false)}
                >
                  View Form
                </button>
              ) : (
                <button
                  type="button"
                  className="button"
                  onClick={() => setIsJsonView(true)}
                >
                  View JSON
                </button>
              )}
            </>
          )}
          <button type="button" className="button" onClick={onClose}>
            cancel
          </button>
          <button
            type="button"
            className={`button is-primary ${loading && 'is-loading'}`}
            onClick={handleSubmit}
          >
            Save
          </button>
        </footer>
      </div>
    </Container>
  );
};

const AddEditForm = withFormik({
  mapPropsToValues: ({ cmsData }) => ({
    name: cmsData ? cmsData.name : '',
    description: cmsData ? cmsData.description : '',
    cms_data_type: cmsData ? cmsData.cms_data_type : '',
  }),

  validationSchema: yup.object().shape({
    name: yup
      .string()
      .nullable('Name is required!')
      .min(3, 'Please enter at least 3 characters')
      .max(50, 'Please enter less than 20 characters')
      .required('Name is required!'),
    description: yup
      .string()
      .nullable('Description is required!')
      .min(3, 'Please enter at least 3 characters')
      .max(50, 'Please enter less than 20 characters')
      .required('Description is required!'),
  }),

  handleSubmit: (values, { props }) => {
    if (!values.cms_data_type) {
      toast.error('Please select a cms data type!');
    } else {
      props.onSubmit({
        name: values.name,
        description: values.description,
        cms_data_type: values.cms_data_type,
      });
    }
  },
  enableReinitialize: true,
  displayName: 'AddEditForm',
})(Form);

export default AddEditForm;
